import React from "react";
// ANT DESIGN COMPONENTS
import { Dropdown, Menu, Space } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsis,
  faTrash,
  faPenToSquare,
  faBan,
  faCalendarDay,
  faFileExport,
} from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useLazyGetExportFullOrganizationQuery } from "../../../redux/organizations/organizationAPI";
import {
  updateOrganization,
  updateOpenDrawerHolidays,
  updateOpenModalDeleteOrganization,
} from "../../../redux/organizations/organizationSlice";

// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
import { Permission, hasPermission } from "../../../utils/validatePermissions";
// COMPONENTS
import TableActionsMenuItem from "../../components/table/TableActionsMenuItem";
// INTERFACES
import { RootState } from "../../../app/store";
import { OrganizationInterface } from "../../../interfaces/Organizations.interface";
// FLATBUFFER DISPLAY
import { formatUuidT } from "../../../flatbuffers/display";

export default function OrganizationsTableActions({
  selectedItem,
}: {
  selectedItem: OrganizationInterface;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { permissionSet } = useSelector((state: RootState) => state.user);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [getExportFullOrganization] = useLazyGetExportFullOrganizationQuery();

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const handleOpenDrawerHolidays = () => {
    dispatch(updateOrganization(selectedItem));
    dispatch(updateOpenDrawerHolidays(true));
  };

  const handleOpenModalDelete = () => {
    dispatch(updateOrganization(selectedItem));
    dispatch(updateOpenModalDeleteOrganization(true));
  };

  const handleEditInWizard = () => {
    navigate(`/organization-management/setup-wizard?orgId=${formatUuidT(selectedItem.id)}`);
  };

  const handleExportFullBinary = async () => {
    try {
      const token = await GETJwtToken();
      const organizationId = formatUuidT(selectedItem.id);
      const result = await getExportFullOrganization({ organizationId, token }).unwrap();

      if (!result || !result.blob) {
        throw new Error("No valid file was received.");
      }

      const headerFileNameMatch = result.contentDisposition.match(/filename="(.+)"/);
      const fileName = headerFileNameMatch
        ? headerFileNameMatch[1]
        : `organization_${organizationId}_${Date.now()}.bin`;

      const blob = new Blob([result.blob], { type: "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      // Global alert/interceptor handles API errors.
    }
  };

  // ************************************************ */
  // USE EFFECT ************************************* */

  // ************************************************ */
  // ROW ACTIONS ************************************ */
  const manageActions = () => {
    const BLANK = {
      label: (
        <div className="generalStyles__tableActionBox">
          <span>
            <FontAwesomeIcon className="generalStyles__error generalStyles__mrFix" icon={faBan} />
            {t("general.noActions")}
          </span>
        </div>
      ),
      key: "0",
    };

    const HOLIDAYS = hasPermission(Permission.HolidaysView, permissionSet ?? [])
      ? {
          label: (
            <TableActionsMenuItem
              icon={faCalendarDay}
              text={t("general.holidays")}
              onClick={handleOpenDrawerHolidays}
            />
          ),
          key: "2",
        }
      : null;

    const EXPORT_FULL_BINARY = hasPermission(Permission.OrgsView, permissionSet ?? [])
      ? {
          label: (
            <span>
              <FontAwesomeIcon
                className="generalStyles__info generalStyles__mrFix"
                icon={faFileExport}
              />
              {t("organizationManagement.exportDescription")}
            </span>
          ),
          key: "6",
          children: [
            {
              label: "Export as Binary (.bin)",
              key: "6-bin",
              onClick: handleExportFullBinary,
            },
          ],
        }
      : null;

    const DELETE = hasPermission(Permission.OrgsDelete, permissionSet ?? [])
      ? {
          label: (
            <TableActionsMenuItem
              icon={faTrash}
              text={t("organizationManagement.delete")}
              onClick={handleOpenModalDelete}
            />
          ),
          key: "4",
        }
      : null;

    const EDIT = hasPermission(Permission.WizardInstallerAccess, permissionSet ?? [])
      ? {
          label: (
            <TableActionsMenuItem
              icon={faPenToSquare}
              text={t("organizationManagement.edit")}
              onClick={handleEditInWizard}
            />
          ),
          key: "5",
        }
      : null;

    // return object with valid actions
    if (DELETE || HOLIDAYS || EDIT || EXPORT_FULL_BINARY) {
      return [EDIT, HOLIDAYS, EXPORT_FULL_BINARY, DELETE];
    }
    return [BLANK];
  };

  const menu = <Menu items={manageActions()} />;

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      <Dropdown overlay={menu} trigger={["click"]}>
        <Space className="generalStyles__hoverCursor">
          <FontAwesomeIcon icon={faEllipsis} />
        </Space>
      </Dropdown>
    </div>
  );
}
